import { NEVER, Subject } from 'rxjs'
import { distinctUntilChanged, map, scan } from 'rxjs/operators'
import { action, computed, observable, makeObservable } from 'mobx'
import { QuestionDTO } from 'jobs/dtos/questions'
import { areDeepEqual } from 'utils/object-util'
import { shareLatest } from 'utils/rx-util'
import { QuestionViewModel } from './component-registry'
import { getQuestionViewModelConstructor } from './type-registry'
import { mergeAnswers } from './util'
import {
  NotifyCallback,
  QuestionViewModelAdapter,
  QuestionViewModelPort,
} from './QuestionViewModelBase'

/**
 * Question list.
 */
export default class QuestionListViewModel {
  /**
   * Subject for notifying of answer changes.
   */
  private notifyAnswerChanges = new Subject()

  /**
   * Shortcut for the notify callback.
   */
  private notifyAnswerChange: NotifyCallback = () =>
    this.notifyAnswerChanges.next(undefined)

  /**
   * Question view models.
   */
  @observable questions: QuestionViewModel[] = []

  /**
   * Whether has a breaker question.
   */
  @computed
  get hasBreaker() {
    return this.questions.some((q) => q.type === 'BREAKER')
  }

  /**
   * Answers as a stream with memory in terms of not discarding irrelevant answers.
   * Only emits when the answers get changed by the user, not just based on the MobX answers
   * observable.
   */
  readonly answersChanged$ = this.notifyAnswerChanges.pipe(
    map(() => this.answers),
    // Only emit when the answers have changed structurally.
    distinctUntilChanged(areDeepEqual),
    // If a previous question is no longer relevant, the `answers` computed won't include it.
    // This means that we would forget the answer. When editing answers however, we want to
    // remember them in case they change their mind by making the question relevant again.
    scan(mergeAnswers, []),
    // Only keep a single subscription, emit the current value on subscribe.
    shareLatest(),
  )

  /**
   * Question VM adapter.
   *
   * @private
   */
  private readonly adapter: QuestionViewModelPort

  /**
   * Constructor.
   *
   * @param opts
   */
  constructor(opts: QuestionViewModelOptions) {
    makeObservable(this)
    this.adapter = QuestionViewModelAdapter(
      this.notifyAnswerChange,
      opts.getAsOfDate,
      opts.documentRequestsAdapterFactory,
    )
  }

  /**
   * Reconciles the questions view models with the DTOs.
   *
   * @param dtos
   */
  @action.bound
  reconcile(dtos: QuestionDTO[]) {
    const newQuestions: QuestionViewModel[] = []

    const inputMap = new Map(dtos.map((dto) => [dto.id, true]))
    this.questions.forEach((existing) => {
      const isInInput = inputMap.get(existing.id)
      if (!isInInput) {
        existing.dispose()
      }
    })

    for (let i = 0; i < dtos.length; i++) {
      const dto = dtos[i]
      const ViewModelConstructor = getQuestionViewModelConstructor(dto.type)
      const existing = this.questions.find((q) => q.id === dto.id)
      if (existing?.type === dto.type) {
        existing.hydrateDTO(dto as any)
        newQuestions.push(existing)
        continue
      }

      const newInstance = new ViewModelConstructor(this.adapter)
      newInstance.hydrateDTO(dto as any)
      newQuestions.push(newInstance)
    }

    this.questions = newQuestions
  }

  /**
   * Validates all the question responses, returns `true` if all are valid, `false` otherwise.
   */
  @action.bound
  validate(): boolean {
    return this.questions
      .map((question) => question.validateResponse().type === 'Success')
      .every(Boolean)
  }

  /**
   * Answers from all questions. Does not include irrelevant answers.
   */
  @computed.struct
  get answers() {
    return this.questions.map((q) => q.answerDTO)
  }

  /**
   * Whether a breaker is currently showing.
   */
  @computed
  get isBroken() {
    return this.questions.some((q) => q.type === 'BREAKER')
  }

  /**
   * Useful as a default.
   */
  static get none(): QuestionListViewModel {
    return noOpQuestionListViewModel
  }
}

/**
 * Options for the question list view model.
 */
export interface QuestionViewModelOptions {
  documentRequestsAdapterFactory: QuestionViewModelPort['documentRequestsAdapterFactory']
  getAsOfDate: QuestionViewModelPort['getAsOfDate']
}

/**
 * Used as a default implementation rather than `null`.
 */
class NoOpQuestionListViewModel extends QuestionListViewModel {
  // Couldn't find a way redefine in mobx 6 since the field is annotated.
  childQuestions: QuestionViewModel[] = []

  readonly answersChanged$ = NEVER

  constructor() {
    super(QuestionViewModelAdapter.none)
  }

  reconcile() {
    // No op
  }

  validate(): boolean {
    return false
  }

  get answers() {
    return []
  }
}

const noOpQuestionListViewModel = new NoOpQuestionListViewModel()
